/**
 * The /out/ hand-off ("Taking you to …"), its rules in one place for the four places that apply
 * them: the inline script in every prerendered /out/ shell (scripts/prerender.mjs), the app's page
 * (src/pages/Go.tsx), the edge (functions/_middleware.ts writes the Continue link for visitors
 * without JavaScript) and the tests (scripts/out-plan.test.mjs).
 *
 * WHY THE SHELL OWNS THE TIMER (2026-10-19). The 2-second timer used to start only once the whole
 * app had loaded, so a tap reached the partner after 3.1–3.4 s on a warm phone and 5.9 s cold, and
 * Continue was a dead '#' link until then. The shell's inline script now runs as the HTML arrives:
 * it sets both Continue links, starts the timer counted from the click itself, and the app steps
 * aside when it finds window.__out.
 *
 * The rules are the ones the /go/ human gate expects (shared/go-gate.mjs): the page a click came
 * from (a same-origin referrer, never an /out/ page) travels as ?ref=, only to the URL the timer or
 * a plain click on Continue navigates to. No ref, no automatic redirect, and Continue reaches the
 * partner untracked. The tracked URL is never put in an href, so nothing that reads the page's
 * links can replay it.
 */

/** How long the card shows before the redirect, counted from the click (ms). 1200 since 2026-10-22 (the owner's option; it was 2000). */
export const REDIRECT_MS = 1200;
/** The card never flashes by: at least this long, however late the shell's script ran (ms). */
export const MIN_WAIT_MS = 300;
/** localStorage key of the hand-off record the price board writes on "Get tickets" (src/lib/handoff.ts). */
export const HANDOFF_KEY = 'tixinfo.out.v1';
/** A hand-off record older than this is ignored and cleared (ms). */
export const HANDOFF_TTL_MS = 120000;
/** A matching record younger than this times the redirect from the click itself, not from when this
 *  page started loading; an older one (an earlier click) never shortens the wait (ms). */
export const CLICK_CLOCK_MS = 10000;
/** localStorage: the kind of site that last sent this browser, {s, t} (src/lib/visit.ts writes it). */
export const SOURCE_KEY = 'tixinfo.src';
/** localStorage: the day of this browser's first visit, YYYY-MM-DD (src/lib/visit.ts writes it). */
export const FIRST_KEY = 'tixinfo.first';
/** A source note older than this no longer describes the visit that clicked (ms). */
export const SOURCE_FRESH_MS = 6 * 3600e3;

/**
 * The click's two coarse tags for /go/ (functions/go/[market].ts counts them, never per click): `s`,
 * the kind of site that sent this browser, when noted in the last 6 hours, and `rv`, 1 when the
 * browser's first visit was before today (UTC), else 0. Each is null when storage cannot tell —
 * "unknown" is then kept apart from "new".
 *
 * Self-contained ES5 like outPlan (inlined into the /out/ shells): the keys and the 6 hours are
 * written out; scripts/out-plan.test.mjs holds them equal to the constants above.
 *
 * @param {{ getItem: (k: string) => string | null }} ls
 * @param {number} now
 * @returns {{ s: string | null, rv: 0 | 1 | null }}
 */
export function visitTags(ls, now) {
  var s = null;
  var rv = null;
  try {
    var src = JSON.parse(ls.getItem('tixinfo.src') || 'null');
    if (src && typeof src.s === 'string' && /^[a-z-]{2,16}$/.test(src.s) && typeof src.t === 'number' && now - src.t >= 0 && now - src.t < 21600000) s = src.s;
  } catch (e) {
    s = null;
  }
  try {
    var first = ls.getItem('tixinfo.first');
    if (first && /^\d{4}-\d{2}-\d{2}$/.test(first)) rv = first < new Date(now).toISOString().slice(0, 10) ? 1 : 0;
  } catch (e) {
    rv = null;
  }
  return { s: s, rv: rv };
}

/**
 * Where the Continue links point (`cont`, never with ref), and where the timer and a plain click on
 * Continue go (`tracked`: cont plus the page the click came from, and the click's coarse `tags` —
 * visitTags — when given; null without a ref).
 *
 * Self-contained and written in plain ES5 syntax on purpose: scripts/prerender.mjs inlines its
 * source into the /out/ shells, where it runs untranspiled on any browser.
 *
 * @param {{ market: string, search: string, referrer: string, origin: string, tags?: { s: string | null, rv: 0 | 1 | null } }} o
 * @returns {{ u: string, event: string, cont: string | null, tracked: string | null }}
 */
export function outPlan(o) {
  var sp = new URLSearchParams(o.search || '');
  var u = sp.get('u') || '';
  var ev = sp.get('event') || '';
  if (!u || !o.market) return { u: u, event: ev, cont: null, tracked: null };
  var p = new URLSearchParams({ u: u });
  if (ev) p.set('event', ev);
  var pl = sp.get('p');
  if (pl) p.set('p', pl);
  var cont = '/go/' + encodeURIComponent(o.market) + '?' + p.toString();
  var ref = null;
  try {
    var r = o.referrer ? new URL(o.referrer) : null;
    if (r && r.origin === o.origin && r.pathname.indexOf('/out/') !== 0) ref = r.pathname.slice(0, 200);
  } catch (e) {
    ref = null;
  }
  var tg = o.tags || {};
  var tail = (tg.s ? '&s=' + encodeURIComponent(tg.s) : '') + (tg.rv === 0 || tg.rv === 1 ? '&rv=' + tg.rv : '');
  return { u: u, event: ev, cont: cont, tracked: ref ? cont + '&ref=' + encodeURIComponent(ref) + tail : null };
}

/**
 * The inline script of the /out/<market> shell, placed right after the #root container (the
 * Continue links exist by then; the app's module script is deferred, so this always runs first).
 *
 *  - sets both Continue links (a[data-continue]) to `cont`;
 *  - window.__out = { cont, tracked, started, wait } tells src/pages/Go.tsx it owns the hand-off
 *    (tracked carries the click's coarse tags, visitTags);
 *  - with a tracked URL: one capture-phase click guard (a plain click on Continue stops the timer
 *    and goes to the tracked URL itself; a click that opens a new tab keeps the untracked href),
 *    and, unless the page was reached by Back/Forward or a reload, the timer: REDIRECT_MS after the
 *    click when the board's hand-off record names this event and partner, else after this page
 *    started loading; never under MIN_WAIT_MS. The progress bar gets the same duration;
 *  - without one: no timer, and <html class="out-manual"> (the card then reads "one click away");
 *    the same when Back restores the page from the back/forward cache (pageshow, persisted).
 *
 * @param {string} market
 */
export const outShellScript = (market) =>
  '(function(){try{' +
  // its own source, line breaks dropped (every statement ends in ; or a brace)
  'var outPlan=' + outPlan.toString().replace(/\s*\n\s*/g, '') + ';' +
  'var visitTags=' + visitTags.toString().replace(/\s*\n\s*/g, '') + ';' +
  'var M=' + JSON.stringify(market) + ',R=' + REDIRECT_MS + ',MIN=' + MIN_WAIT_MS + ',KEY=' + JSON.stringify(HANDOFF_KEY) + ',CLK=' + CLICK_CLOCK_MS + ';' +
  'var tg={s:null,rv:null};try{tg=visitTags(localStorage,Date.now());}catch(e){}' +
  'var d=document,h=d.documentElement,q=outPlan({market:M,search:location.search,referrer:d.referrer,origin:location.origin,tags:tg});' +
  'if(!q.cont)return;' +
  'var as=d.querySelectorAll("a[data-continue]"),i;for(i=0;i<as.length;i++)as[i].setAttribute("href",q.cont);' +
  'var w=window.__out={cont:q.cont,tracked:q.tracked,started:false,wait:0};' +
  'if(!q.tracked){h.classList.add("out-manual");return;}' +
  'var t=0;' +
  // Back after a Continue tap restores this page from the back/forward cache with no timer: say so
  'if(window.addEventListener)window.addEventListener("pageshow",function(e){if(e.persisted){clearTimeout(t);h.classList.add("out-manual");}});' +
  'd.addEventListener("click",function(e){var a=e.target&&e.target.closest?e.target.closest("a"):null;if(!a||a.getAttribute("href")!==q.cont)return;clearTimeout(t);' +
  'if(e.button!==0||e.metaKey||e.ctrlKey||e.shiftKey||e.altKey)return;e.preventDefault();location.assign(q.tracked);},true);' +
  'var pe=window.performance,nv=pe&&pe.getEntriesByType?pe.getEntriesByType("navigation")[0]:null,nt=nv?nv.type:(pe&&pe.navigation?["navigate","reload","back_forward"][pe.navigation.type]:"navigate");' +
  'if(nt==="back_forward"||nt==="reload"){h.classList.add("out-manual");return;}' +
  'var el=pe&&pe.now?pe.now():0,x=null;try{x=JSON.parse(localStorage.getItem(KEY)||"null");}catch(e){}' +
  'if(x&&x.event===q.event&&x.market===M&&typeof x.ts==="number"){var age=Date.now()-x.ts;if(age>=0&&age<CLK&&age>el)el=age;}' +
  'var wait=Math.max(MIN,Math.round(R-el)),bar=d.querySelector(".redirect-bar");if(bar)bar.style.setProperty("--redirect-ms",wait+"ms");' +
  't=setTimeout(function(){location.replace(q.tracked);},wait);w.started=true;w.wait=wait;' +
  '}catch(e){}})();';
